import { Chip } from '@mui/material';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';
import { Trans } from 'react-i18next';
import i18n from 'config/i18n';
import { t } from 'i18next';
import StatusBadge from 'components/ui/StatusBadge';

// MUI palette rangi -> StatusBadge varianti (yumshoq fon + rangli matn)
const STATUS_VARIANT_MAP = {
  success: 'success',
  warning: 'warning',
  error: 'danger',
  danger: 'danger',
  primary: 'primary',
  secondary: 'primary', // ko'rinmas kulrang emas — yumshoq binafsha
  info: 'info', // moviy — secondary (binafsha) dan farqlansin (masalan P2P vs CARD)
  default: 'neutral',
};

const setTableCellType = (column, item, row) => {
  switch (column.type) {
    case 'multipleFields': {
      if (column.fields) {
        return (
          <div className='flex flex-col'>
            {column.fields.map((item) =>
              row.hasOwnProperty(item) ? (
                <div className='my-0.5' key={item}>
                  {row[item]}
                </div>
              ) : (
                ''
              )
            )}
          </div>
        );
      }
      return '';
    }
    case 'simpleTranslate': {
      return t(`${column.chainStart}.${item}`);
    }
    case 'date': {
      return moment(item).format('DD.MM.YYYY');
    }
    case 'dateTime': {
      return moment(item).format('DD.MM.YYYY HH:mm');
    }
    case 'status': {
      return item ? (
        <StatusBadge variant='success'>{column.label.true}</StatusBadge>
      ) : (
        <StatusBadge variant='neutral'>{column.label.false}</StatusBadge>
      );
    }
    case 'customStatus': {
      if (column.variants && column.variants.length > 0 && !isNaN(item)) {
        let result = column.variants.filter((variant) => variant.code == item);
        if (result.length > 0) {
          return (
            <StatusBadge
              variant={STATUS_VARIANT_MAP[result[0].color] || 'neutral'}
            >
              {i18n.t(result[0].label)}
            </StatusBadge>
          );
        }
      }
      return '';
    }
    case 'strong': {
      // Asosiy identifikator (masalan shartnoma raqami) — ko'zga birinchi tashlanadi
      return (
        <span className='font-medium text-ink whitespace-nowrap'>{item}</span>
      );
    }
    case 'customSuffix': {
      return item ? `${item}${column?.suffix}` : '';
    }
    case 'customPrefix': {
      return item ? `${column?.prefix}${item}` : '';
    }
    case 'price': {
      return (
        <NumericFormat
          value={item}
          displayType={'text'}
          allowNegative={false}
          thousandSeparator={' '}
          decimalScale={3}
          className='bg-transparent whitespace-nowrap'
          suffix={' UZS'}
        />
      );
    }
    case 'priceCurrency': {
      return (
        <NumericFormat
          value={(parseFloat(item) * 10) % 10 > 0 ? item : parseInt(item)}
          displayType={'text'}
          allowNegative={false}
          thousandSeparator={' '}
          decimalScale={1}
          className='bg-transparent whitespace-nowrap'
          suffix={row?.isvalute == '1' ? ' $' : ' UZS'}
        />
      );
    }
    case 'conditionalPrice': {
      const matches = column.conditionField
        ? row?.[column.conditionField] == column.conditionValue
        : true;
      if (matches) {
        return (
          <NumericFormat
            value={item}
            displayType={'text'}
            allowNegative={false}
            thousandSeparator={' '}
            decimalScale={2}
            className='bg-transparent whitespace-nowrap'
            suffix={column?.suffix || ''}
          />
        );
      }
      return item;
    }
    case 'kpiCountType': {
      // KPI hisoblash turi: Foiz / Summa / Oraliqli (progressiv)
      if (row?.tiers?.length > 0) {
        return (
          <StatusBadge variant='primary'>
            {t('settings.staffKpi.tiers.tiered')} · {row.tiers.length}
          </StatusBadge>
        );
      }
      return (
        <StatusBadge variant={row?.count_type == 1 ? 'success' : 'neutral'}>
          {t(
            `settings.staffKpi.countType.${
              row?.count_type == 1 ? 'procent' : 'sum'
            }`
          )}
        </StatusBadge>
      );
    }
    case 'kpiRate': {
      // KPI qiymati: foizga % belgisi, ortiqcha nollarsiz; oraliqli bo'lsa ro'yxat
      const fmtRate = (countType, price) => {
        const num = Number(price);
        const val = Number.isFinite(num) ? Number(num.toFixed(5)) : price;
        return countType == 1
          ? `${val}%`
          : Number(val).toLocaleString('ru-RU');
      };
      if (row?.tiers?.length > 0) {
        return (
          <div className='flex flex-col gap-0.5'>
            {row.tiers.map((tier, i) => (
              <span
                key={tier?.id || i}
                className='text-[12px] whitespace-nowrap text-gray-600'
              >
                {tier.from_count}–{tier.to_count ?? '∞'}:{' '}
                <b className='text-gray-800'>
                  {fmtRate(tier.count_type, tier.price)}
                </b>
              </span>
            ))}
          </div>
        );
      }
      return (
        <span className='whitespace-nowrap font-medium'>
          {fmtRate(row?.count_type, item)}
        </span>
      );
    }
    case 'priceValute': {
      return (
        <NumericFormat
          value={(parseFloat(item) * 10) % 10 > 0 ? item : parseInt(item)}
          displayType={'text'}
          allowNegative={false}
          thousandSeparator={' '}
          decimalScale={1}
          className='bg-transparent whitespace-nowrap'
          suffix={' $'}
        />
      );
    }
    case 'area': {
      if (item) {
        return (
          <span className='whitespace-nowrap'>
            <NumericFormat
              value={item}
              displayType={'text'}
              allowNegative={false}
              thousandSeparator={''}
              decimalScale={2}
              className='bg-transparent whitespace-nowrap'
            />{' '}
            <Trans i18nKey='common.global.meter'>
              m<sup>2</sup>
            </Trans>
          </span>
        );
      }
      return '';
    }
    case 'link': {
      return (
        <code>
          <Link
            to={item}
            // target='_blank'
          >
            {item}
          </Link>
        </code>
      );
    }
    case 'multiply': {
      return column.childStrings.reduce((acc, el, index) => {
        let value = el
          ? !isNaN(getObjField(row[column.fields[index]], el))
            ? parseInt(getObjField(row[column.fields[index]], el))
            : 1
          : parseInt(row[column.fields[index]]);
        return acc * value;
      }, 1);
    }
    case 'nested': {
      return getObjField(item, column.childStr);
    }
    case 'nestedChain': {
      return column.childStrings
        .map((el) => {
          return getObjField(item, el);
        })
        .join(' ');
    }
    case 'nestedBadge': {
      return (
        <Chip
          label={getObjField(item, column.childStr)}
          variant='tableBadge'
          color={column?.color || 'secondary'}
        />
      );
    }
    default: {
      return null;
    }
  }
};

function getField(obj, field) {
  return obj[field];
}

function getObjField(obj, str) {
  if (obj) {
    let a = str.split('.');
    let b = obj;
    for (let i = 0; i < a.length; i++) {
      b = getField(b, a[i]);
    }
    return b;
  }
  return '';
}

export default setTableCellType;
